import { Feature, Map } from "ol";
import { Coordinate } from "ol/coordinate";
import { Geometry, LineString, MultiPoint, Point, Polygon } from "ol/geom";
import { toLonLat } from "ol/proj";
import { Circle, Fill, Stroke, Style } from "ol/style";
import { ANNOTATION_COLOR } from "../Map/constants";
import { ANNOTATION_SELECT_STYLE } from "src/domains/shared/constants/annotationSelectStyle";
import { makeText } from "../Map/utils/object";

/**
 * 2D 선택 핸들의 번호 색 — 흰 외곽선(`makeText` 의 `outline`)을 두른 검정 글자다.
 * 3D 가 읽는 `ANNOTATION_SELECT_STYLE.handle.numberColor`(흰색)와 값이 다르므로 공용 토큰에서 읽지 않는다.
 */
const SELECT_HANDLE_NUMBER_COLOR = "black";

export const makeSelectedFeature = (nomalizedCoordinates: Coordinate[]) => {
  const multiPointGeometry = new MultiPoint(nomalizedCoordinates);

  const features = multiPointGeometry
    .getPoints()
    .map((point, index): Feature<Geometry> => {
      const text = index + 1; // 순번 설정
      const style = new Style({
        image: new Circle({
          radius: ANNOTATION_SELECT_STYLE.handle.radiusPx,
          fill: new Fill({
            color: ANNOTATION_COLOR["SELECT"].stroke(),
          }),
          stroke: new Stroke({
            color: ANNOTATION_COLOR["SELECT"].stroke(),
            width: ANNOTATION_SELECT_STYLE.handle.strokeWidthPx,
          }),
        }),
        text: makeText({
          text: String(text),
          size: ANNOTATION_SELECT_STYLE.handle.numberFontPx,
          color: SELECT_HANDLE_NUMBER_COLOR,
          outline: true,
          isMarker: false,
        }),
        zIndex: 1000,
      });
      const textStyle = style.getText();
      if (textStyle) {
        textStyle.setText(text.toString());
      }
      const pointFeature = new Feature(point);
      pointFeature.setStyle(style);

      return pointFeature;
    });

  return features;
};

export const positionsFromFeature = (
  feature: Feature<Geometry>,
  lonlat?: boolean
) => {
  const geometry = feature.getGeometry();
  if (geometry instanceof Polygon) {
    if (lonlat) {
      return geometry
        .getCoordinates()
        .map((group) => group.map((coord) => toLonLat(coord)));
    }
    return geometry.getCoordinates();
  }
  if (geometry instanceof LineString) {
    if (lonlat) {
      return geometry.getCoordinates().map((coord) => toLonLat(coord));
    }
    return geometry.getCoordinates();
  }
  if (geometry instanceof Point) {
    if (lonlat) {
      return toLonLat(geometry.getFirstCoordinate());
    }
    return geometry.getFirstCoordinate();
  }
  if (geometry instanceof MultiPoint) {
    if (lonlat) {
      return toLonLat(geometry.getFirstCoordinate());
    }
    return geometry.getFirstCoordinate();
  }
};

export const positionsFromMultiPointFeatures = (
  features: Feature<Geometry>[]
) => {
  return features.map((feat) => positionsFromFeature(feat));
};

export function fitFeatureToView(map: Map, feature: Feature<Geometry>) {
  const geometry = feature.getGeometry();
  if (!geometry) return;

  const extent = geometry.getExtent();

  map.getView().fit(extent, {
    padding: [200, 200, 200, 200],
    duration: 500,
  });
}
